import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText } from 'lucide-react';
import { reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import Receipt from '@/components/pos/Receipt';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

export default function SaleActivityInvoiceSection() {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [cashier, setCashier] = useState('');
  const [page, setPage] = useState(0);
  const [invoice, setInvoice] = useState('');

  const { data: cashiers = [] } = useQuery({
    queryKey: ['reportCashiers'],
    queryFn: () => reportsApi.getReportCashiers(),
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['saleActivityInvoice', from_date, to_date, cashier],
    queryFn: () =>
      reportsApi.getSaleActivityInvoice({
        from_date,
        to_date,
        cashier: cashier || undefined,
      }),
  });

  useEffect(() => {
    setPage(0);
  }, [rows, from_date, to_date, cashier]);

  const handleExport = () => {
    if (!rows.length) return;
    downloadCsv(
      `sale-activity-invoice-${from_date}-${to_date}.csv`,
      [
        { key: 'invoice', label: 'Invoice' },
        { key: 'date', label: 'Date' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'item_count', label: 'Item Count' },
        { key: 'qty', label: 'Qty' },
        { key: 'gross', label: 'Gross' },
        { key: 'discount', label: 'Discount' },
        { key: 'tax', label: 'Tax' },
        { key: 'net', label: 'Net' },
      ],
      rows as Record<string, unknown>[]
    );
  };

  const pageRows = rows.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE);

  return (
    <>
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Sale Activity — Invoice Wise</h2>
            <p className="text-sm text-gray-500 mt-1">Invoice-level sale activity for the selected period</p>
          </div>
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>

        <div className="p-5 bg-gray-50/50 border-b border-gray-100 space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />

            <div className="space-y-1.5 flex-1 min-w-[160px]">
              <label className="text-sm font-medium text-gray-700">Cashier</label>
              <select className="input-field" value={cashier} onChange={(e) => setCashier(e.target.value)}>
                <option value="">All Cashiers</option>
                {(cashiers as { user: string; full_name: string }[]).map((c) => (
                  <option key={c.user} value={c.user}>
                    {c.full_name || c.user}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[420px]">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : rows.length > 0 ? (
            <table className="w-full text-sm text-left min-w-[860px]">
              <thead className="text-xs text-gray-500 bg-gray-50 uppercase sticky top-0 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 font-medium">Invoice</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium text-right">Item Count</th>
                  <th className="px-4 py-3 font-medium text-right">Qty</th>
                  <th className="px-4 py-3 font-medium text-right">Gross</th>
                  <th className="px-4 py-3 font-medium text-right">Discount</th>
                  <th className="px-4 py-3 font-medium text-right">Tax</th>
                  <th className="px-4 py-3 font-medium text-right">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((row: any) => (
                  <tr key={row.invoice} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        type="button"
                        className="text-primary-600 hover:text-primary-700 font-medium"
                        onClick={() => setInvoice(row.invoice)}
                      >
                        {row.invoice}
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {row.date ? format(new Date(row.date), 'd MMM yyyy') : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-900">{row.customer_name}</td>
                    <td className="px-4 py-3 text-right">{row.item_count}</td>
                    <td className="px-4 py-3 text-right">{row.qty}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(row.gross)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.discount)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.tax)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatMoney(row.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No invoice activity for the selected filters</p>
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={rows.length} onPageChange={setPage} />
          </div>
        )}
      </div>

      {invoice && <Receipt invoiceName={invoice} onClose={() => setInvoice('')} />}
    </>
  );
}
